import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Item } from '../types';


export const PRICE_FOR_LETTER = process.env.PRICE_FOR_LETTER ? parseFloat(process.env.PRICE_FOR_LETTER) : 5;

export interface CartItem extends Item {
  quantity: number;
  customLabel?: string;
  labelColor?: string;
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Item }
  | { type: 'REMOVE_ITEM'; payload: number }
  | { type: 'UPDATE_QUANTITY'; payload: { id: number; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'UPDATE_CUSTOM_LABEL'; payload: { id: number; customLabel: string } }
  | { type: 'UPDATE_LABEL_COLOR'; payload: { id: number; labelColor: string } }

interface CartContextType {
  state: CartState;
  addItem: (item: Item) => void;
  removeItem: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
  isInCart: (id: number) => boolean;
  updateCustomLabel: (id: number, customLabel: string) => void;
  updateLabelColor: (id: number, labelColor: string) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.id === action.payload.id);

      if (existingItem) {
        const updatedItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );

        return {
          ...state,
          items: updatedItems,
          totalItems: state.totalItems + 1,
          totalPrice: state.totalPrice + action.payload.price,
        };
      } else {
        const newItem = { ...action.payload, quantity: 1 };
        return {
          ...state,
          items: [...state.items, newItem],
          totalItems: state.totalItems + 1,
          totalPrice: state.totalPrice + action.payload.price,
        };
      }
    }

    case 'REMOVE_ITEM': {
      const itemToRemove = state.items.find(item => item.id === action.payload);
      if (!itemToRemove) return state;

      const updatedItems = state.items.filter(item => item.id !== action.payload);
      return {
        ...state,
        items: updatedItems,
        totalItems: state.totalItems - itemToRemove.quantity,
        totalPrice: state.totalPrice - (itemToRemove.price * itemToRemove.quantity),
      };
    }

    case 'UPDATE_QUANTITY': {
      const { id, quantity } = action.payload;
      const item = state.items.find(item => item.id === id);

      if (!item || quantity < 1) return state;

      const quantityDifference = quantity - item.quantity;
      const updatedItems = state.items.map(item =>
        item.id === id ? { ...item, quantity } : item
      );

      return {
        ...state,
        items: updatedItems,
        totalItems: state.totalItems + quantityDifference,
        totalPrice: state.totalPrice + (item.price * quantityDifference),
      };
    }

    case 'CLEAR_CART':
      return {
        items: [],
        totalItems: 0,
        totalPrice: 0,
      };
    case 'UPDATE_CUSTOM_LABEL': {
      const { id, customLabel } = action.payload;
      const item = state.items.find(item => item.id === id);

      if (!item) return state;

      const updatedItems = state.items.map(item =>
        item.id === id ? { ...item, customLabel } : item
      );

      return {
        ...state,
        totalPrice: state.totalPrice + ((customLabel ? customLabel.split(' ').join('').length * PRICE_FOR_LETTER : 0) - (item.customLabel ? item.customLabel.split(' ').join('').length * PRICE_FOR_LETTER : 0)) * item.quantity,
        items: updatedItems,
      };
    }

    case 'UPDATE_LABEL_COLOR': {
      const { id, labelColor } = action.payload;
      const item = state.items.find(item => item.id === id);

      if (!item) return state;

      const updatedItems = state.items.map(item =>
        item.id === id ? { ...item, labelColor } : item
      );

      return {
        ...state,
        items: updatedItems,
      };
    }

    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    totalItems: 0,
    totalPrice: 0,
  });

  const addItem = (item: Item) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  };

  const removeItem = (id: number) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const updateQuantity = (id: number, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };
  const updateCustomLabel = (id: number, customLabel: string) => {
    dispatch({
      type: 'UPDATE_CUSTOM_LABEL',
      payload: { id, customLabel },
    });
  };
  const updateLabelColor = (id: number, labelColor: string) => {
    dispatch({
      type: 'UPDATE_LABEL_COLOR',
      payload: { id, labelColor },
    });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const isInCart = (id: number) => {
    return state.items.some(item => item.id === id);
  };

  return (
    <CartContext.Provider
      value={{
        state,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        isInCart,
        updateCustomLabel,
        updateLabelColor
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
